import {
    useAPIQuery,
    useGlobalAPIQueryContext,
} from "@dev2_techwalnut/tw-api-query-hook";
import { Table } from "@dev2_techwalnut/tw-react-crud-dashlite";
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";

const MOVEMENT_OPTIONS = [
    { value: "", label: "All movements" },
    { value: "opening_balance", label: "Opening balance" },
    { value: "wip_qc_accept", label: "WIP QC accepted" },
    { value: "po_qc_accept", label: "PO QC accepted" },
    { value: "requisition_allocation", label: "Allotted to packaging" },
    { value: "requisition_issue", label: "Issued to packaging" },
    { value: "material_return", label: "Returned to stock / WIP plan" },
    { value: "requisition_allocation_release", label: "Allotment released" },
    { value: "daily_plan_allocation_release", label: "Planning capacity released" },
    { value: "build_order_allocation", label: "Build order allocation" },
    { value: "sales_order_allocation", label: "Sales order allocation" },
    { value: "dispatch", label: "Dispatched" },
    { value: "adjustment", label: "Adjustment" },
    { value: "fg_production_from_wip", label: "FG produced from WIP" },
];

const formatTimestamp = (value) => {
    if (!value) return "—";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleString();
};

const formatQty = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return num.toLocaleString(undefined, { maximumFractionDigits: 4 });
};

const timelineColumns = [
    {
        accessor: "created_at",
        header: "When",
        cell: (row) => formatTimestamp(row?.created_at),
    },
    {
        accessor: "movement_label",
        header: "Movement",
        cell: (row) => row?.movement_label || row?.movement_type || "—",
    },
    {
        accessor: "quantity",
        header: "Qty",
        cell: (row) => formatQty(row?.quantity),
    },
    {
        accessor: "direction",
        header: "Direction",
        cell: (row) => row?.direction || "—",
    },
    {
        accessor: "source_bucket",
        header: "From",
        cell: (row) => row?.source_bucket || "—",
    },
    {
        accessor: "target_bucket",
        header: "To",
        cell: (row) => row?.target_bucket || "—",
    },
    {
        accessor: "source_daily_plan_code",
        header: "Source DP",
        cell: (row) =>
            row?.source_daily_plan_code || row?.source_batch_number || "—",
    },
    {
        accessor: "target_daily_plan_code",
        header: "Target DP",
        cell: (row) =>
            row?.target_daily_plan_code || row?.target_batch_number || "—",
    },
    {
        accessor: "requisition_code",
        header: "Requisition",
        cell: (row) => row?.requisition_code || "—",
    },
    {
        accessor: "packaging_daily_plan_code",
        header: "Packaging DP",
        cell: (row) => row?.packaging_daily_plan_code || "—",
    },
    {
        accessor: "posted_by_username",
        header: "Posted by",
        cell: (row) => row?.posted_by_username || row?.created_by || "—",
    },
];

const StockLedger = () => {
    const { apiClient } = useGlobalAPIQueryContext();
    const [searchParams, setSearchParams] = useSearchParams();

    const [itemId, setItemId] = useState(searchParams.get("item_id") || "");
    const [fromDate, setFromDate] = useState(searchParams.get("from") || "");
    const [toDate, setToDate] = useState(searchParams.get("to") || "");
    const [movementType, setMovementType] = useState(
        searchParams.get("movement_type") || ""
    );
    const [requisitionId, setRequisitionId] = useState(
        searchParams.get("requisition_id") || ""
    );
    const [q, setQ] = useState(searchParams.get("q") || "");
    const [entries, setEntries] = useState(
        Number(searchParams.get("entries")) || 20
    );
    const [pageIndex, setPageIndex] = useState(
        (Number(searchParams.get("page")) || 1) - 1
    );
    const [isExporting, setIsExporting] = useState(false);
    const [itemOptions, setItemOptions] = useState([]);

    const { useFetch: useFetchGeneric } = useAPIQuery({
        key: "api/generic",
        endpoint: "/api/generic/",
    });
    const { data: genericData } = useFetchGeneric({
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 5,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
    });

    useEffect(() => {
        const items = genericData?.item || genericData?.results?.data?.item;
        if (Array.isArray(items)) {
            setItemOptions(
                items.map((api) => ({
                    value: String(api.id),
                    label: api.item_name
                        ? `${api.item_name}${
                              api.item_code ? ` (${api.item_code})` : ""
                          }`
                        : String(api.id),
                }))
            );
        }
    }, [genericData]);

    const selectedItem = useMemo(
        () => itemOptions.find((opt) => opt.value === String(itemId)) || null,
        [itemOptions, itemId]
    );

    const queryParams = useMemo(
        () => ({
            item_id: itemId || undefined,
            from: fromDate || undefined,
            to: toDate || undefined,
            movement_type: movementType || undefined,
            requisition_id: requisitionId || undefined,
            q: q || undefined,
        }),
        [itemId, fromDate, toDate, movementType, requisitionId, q]
    );

    const { useFetch } = useAPIQuery({
        key: "api/stock-ledger",
        endpoint: "/api/stock-ledger/",
    });

    const { data, isLoading, isFetching } = useFetch({
        params: {
            ...queryParams,
            page_no: pageIndex + 1,
            entries,
        },
        enabled: !!itemId,
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 30,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
    });

    const summary = data?.summary || null;
    const itemInfo = data?.item || null;
    const rows = Array.isArray(data?.results?.data) ? data.results.data : [];
    const openLocks = Array.isArray(summary?.open_locks)
        ? summary.open_locks
        : [];

    const updateParams = (mutate) => {
        setPageIndex(0);
        setSearchParams((params) => {
            mutate(params);
            params.set("page", "1");
            return params;
        });
    };

    const handleItemChange = (option) => {
        const value = option?.value ? String(option.value) : "";
        setItemId(value);
        updateParams((params) => {
            if (value) params.set("item_id", value);
            else params.delete("item_id");
        });
    };

    const handleFromChange = (value) => {
        setFromDate(value);
        updateParams((params) => {
            if (value) params.set("from", value);
            else params.delete("from");
        });
    };

    const handleToChange = (value) => {
        setToDate(value);
        updateParams((params) => {
            if (value) params.set("to", value);
            else params.delete("to");
        });
    };

    const handleMovementChange = (value) => {
        setMovementType(value);
        updateParams((params) => {
            if (value) params.set("movement_type", value);
            else params.delete("movement_type");
        });
    };

    const handleRequisitionChange = (value) => {
        const trimmed = value?.trim() || "";
        setRequisitionId(trimmed);
        updateParams((params) => {
            if (trimmed) params.set("requisition_id", trimmed);
            else params.delete("requisition_id");
        });
    };

    const handleSearchChange = (value) => {
        const trimmed = value?.trim() || "";
        setQ(trimmed);
        updateParams((params) => {
            if (trimmed) params.set("q", trimmed);
            else params.delete("q");
        });
    };

    const handlePagination = (value) => {
        setSearchParams((params) => {
            params.set("entries", String(value.pageSize));
            params.set("page", String(value.pageIndex + 1));
            return params;
        });
        setPageIndex(value.pageIndex);
        setEntries(value.pageSize);
    };

    const handleExport = async () => {
        if (!itemId) {
            Swal.fire({
                title: "Select an item",
                text: "Choose an item before exporting the ledger.",
                icon: "info",
            });
            return;
        }
        try {
            setIsExporting(true);
            const response = await apiClient.get("/api/stock-ledger/", {
                params: { ...queryParams, export: "csv" },
                responseType: "blob",
            });
            const contentDisposition = response.headers["content-disposition"];
            const filename =
                contentDisposition
                    ?.split("filename=")?.[1]
                    ?.replaceAll('"', "")
                    ?.trim() || `stock-ledger-item-${itemId}.csv`;
            const href = URL.createObjectURL(
                new Blob([response.data], { type: "text/csv" })
            );
            const link = document.createElement("a");
            link.href = href;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(href);
        } catch (error) {
            Swal.fire({
                title: "Download failed",
                text:
                    error?.response?.data?.message ||
                    error.message ||
                    "Unable to export stock ledger",
                icon: "error",
            });
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <>
            <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between flex-wrap g-3">
                    <div className="nk-block-head-content">
                        <h3 className="nk-block-title page-title">
                            Stock Ledger
                        </h3>
                        <p className="text-soft mb-0">
                            Item timeline of allotments, issues, returns, and QC
                            — including stock on closed packaging plans.
                        </p>
                    </div>
                    <div className="nk-block-head-content">
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={handleExport}
                            disabled={!itemId || isExporting}
                        >
                            {isExporting ? "Exporting…" : "Export CSV"}
                        </button>
                    </div>
                </div>
            </div>

            <div className="card card-bordered mb-3">
                <div className="card-inner">
                    <div className="row g-3 align-items-end">
                        <div className="col-md-4">
                            <label className="form-label">Item</label>
                            <Select
                                options={itemOptions}
                                value={selectedItem}
                                onChange={handleItemChange}
                                isClearable
                                placeholder="Select item…"
                                classNamePrefix="react-select"
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">From</label>
                            <input
                                type="date"
                                className="form-control"
                                value={fromDate}
                                onChange={(e) =>
                                    handleFromChange(e.target.value)
                                }
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">To</label>
                            <input
                                type="date"
                                className="form-control"
                                value={toDate}
                                onChange={(e) => handleToChange(e.target.value)}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Movement</label>
                            <select
                                className="form-select"
                                value={movementType}
                                onChange={(e) =>
                                    handleMovementChange(e.target.value)
                                }
                            >
                                {MOVEMENT_OPTIONS.map((opt) => (
                                    <option key={opt.value || "all"} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Requisition ID</label>
                            <input
                                type="text"
                                className="form-control"
                                value={requisitionId}
                                placeholder="e.g. 12899"
                                onChange={(e) =>
                                    handleRequisitionChange(e.target.value)
                                }
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">Search</label>
                            <input
                                type="search"
                                className="form-control"
                                value={q}
                                placeholder="RQ code, DP code, movement…"
                                onChange={(e) =>
                                    handleSearchChange(e.target.value)
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>

            {!itemId ? (
                <div className="card card-bordered">
                    <div className="card-inner text-center py-5">
                        <p className="text-soft mb-0">
                            Select an item to view its stock ledger.
                        </p>
                    </div>
                </div>
            ) : (
                <>
                    {(isLoading || isFetching) && (
                        <div className="mb-2">
                            <div
                                className="spinner-border spinner-border-sm text-primary"
                                role="status"
                            >
                                <span className="sr-only" />
                            </div>
                            <span className="ms-2 text-soft">Loading ledger…</span>
                        </div>
                    )}

                    {itemInfo && (
                        <div className="mb-2">
                            <strong>{itemInfo.item_name}</strong>
                            {itemInfo.item_code ? (
                                <span className="text-soft">
                                    {" "}
                                    ({itemInfo.item_code})
                                </span>
                            ) : null}
                            {itemInfo.category ? (
                                <span className="badge bg-outline-secondary ms-2">
                                    {itemInfo.category}
                                </span>
                            ) : null}
                        </div>
                    )}

                    {summary && (
                        <>
                            <div className="row g-3 mb-3">
                                <div className="col-sm-6 col-lg-3">
                                    <div className="card card-bordered h-100">
                                        <div className="card-inner">
                                            <div className="text-soft overline-title">
                                                Free (allottable)
                                            </div>
                                            <div className="h4 mb-1">
                                                {formatQty(summary.free_quantity)}
                                            </div>
                                            <div className="fs-12px text-soft">
                                                Sum of source batch/DP balances
                                                available to allot. Closed-plan
                                                issues still appear below.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-6 col-lg-3">
                                    <div className="card card-bordered h-100">
                                        <div className="card-inner">
                                            <div className="text-soft overline-title">
                                                On-hand (source)
                                            </div>
                                            <div className="h4 mb-0">
                                                {formatQty(
                                                    summary.on_hand_source_quantity
                                                )}
                                            </div>
                                            <div className="fs-12px text-soft">
                                                {summary.source_model || "—"}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-6 col-lg-3">
                                    <div className="card card-bordered h-100">
                                        <div className="card-inner">
                                            <div className="text-soft overline-title">
                                                Item.stock
                                            </div>
                                            <div className="h4 mb-0">
                                                {formatQty(
                                                    summary.item_stock_quantity
                                                )}
                                            </div>
                                            <div className="fs-12px text-soft">
                                                May lag source balances
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-6 col-lg-3">
                                    <div className="card card-bordered h-100">
                                        <div className="card-inner">
                                            <div className="text-soft overline-title">
                                                Locked unissued
                                            </div>
                                            <div className="h4 mb-0">
                                                {formatQty(
                                                    summary.locked_unissued_quantity
                                                )}
                                            </div>
                                            <div className="fs-12px text-soft">
                                                Allotted minus issued
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card card-bordered mb-3">
                                <div className="card-inner">
                                    <h6 className="title mb-3">
                                        Requisition locks / issued holdings
                                    </h6>
                                    {openLocks.length === 0 ? (
                                        <p className="text-soft mb-0">
                                            No requisition locks for this item.
                                        </p>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-sm">
                                                <thead>
                                                    <tr>
                                                        <th>Requisition</th>
                                                        <th>Packaging DP</th>
                                                        <th>Status</th>
                                                        <th>Received</th>
                                                        <th>Allotted</th>
                                                        <th>Issued</th>
                                                        <th>Returned</th>
                                                        <th>Net issued</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {openLocks.map((lock) => (
                                                        <tr
                                                            key={
                                                                lock.requisition_id
                                                            }
                                                        >
                                                            <td>
                                                                {
                                                                    lock.requisition_code
                                                                }
                                                            </td>
                                                            <td>
                                                                {lock.packaging_daily_plan_code ||
                                                                    "—"}
                                                            </td>
                                                            <td>
                                                                {lock.packaging_is_closed ? (
                                                                    <span className="badge bg-warning">
                                                                        Closed
                                                                    </span>
                                                                ) : (
                                                                    <span className="badge bg-success">
                                                                        Open
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {formatQty(
                                                                    lock.received_quantity
                                                                )}
                                                            </td>
                                                            <td>
                                                                {formatQty(
                                                                    lock.allocated_quantity
                                                                )}
                                                            </td>
                                                            <td>
                                                                {formatQty(
                                                                    lock.issued_quantity
                                                                )}
                                                            </td>
                                                            <td>
                                                                {formatQty(
                                                                    lock.returned_quantity
                                                                )}
                                                            </td>
                                                            <td>
                                                                {formatQty(
                                                                    lock.net_issued_quantity
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    <div className="card card-bordered">
                        <div className="card-inner">
                            <h6 className="title mb-3">Timeline</h6>
                            <Table
                                data={rows}
                                columns={timelineColumns}
                                pagination
                                pageIndex={pageIndex}
                                pageSize={entries}
                                rowCount={data?.count || 0}
                                globalSearch={false}
                                editRows={false}
                                deleteRows={false}
                                filterModal={false}
                                selectRows={false}
                                onPaginationChange={handlePagination}
                            />
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default StockLedger;
